import React, { useState, useMemo } from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { hotelInfo } from '../mockData';
import { useToast } from '../hooks/use-toast';
import { useSEO } from '../hooks/useSEO';
import contactImage from '../assets/images/contactus.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { toast } = useToast();
  const [sending, setSending] = useState(false);

  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: hotelInfo.name,
    telephone: hotelInfo.contact.phone,
    email: hotelInfo.contact.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '14180 Gunhill Avenue',
      addressLocality: 'Harare',
      addressCountry: 'ZW'
    }
  }), []);

  useSEO({
    title: 'Contact Us | OlivOak Villa',
    description: 'Get in touch with OlivOak Villa, Harare. Call +263 78 937 4888, email reservations@olivoakvilla.com, or use our contact form. We\'d love to hear from you.',
    jsonLd
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    const form = e.target;
    const payload = {
      firstName: form.firstName.value,
      lastName: form.lastName.value,
      email: form.email.value,
      phone: form.phone.value,
      message: form.message.value,
    };

    try {
      const res = await fetch(`${API_URL}/api/contact/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Something went wrong');
      }

      toast({
        title: "Message Sent",
        description: "We'll get back to you within 24 hours. A confirmation has been sent to your email.",
      });
      form.reset();
    } catch (error) {
      toast({
        title: "Failed to send",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh', paddingTop: '80px' }}>
      <section
        className="relative py-24"
        style={{         
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${contactImage}')`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center'
        }}
      >
        <div className="container text-center">
          <h1 className="hero-large mb-4" style={{ color: '#ffffff' }}>
            Contact Us
          </h1>
          <p className="body-large" style={{ color: '#f0f0f0' }}>
            We're here to help
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="heading-1 mb-8">Get in Touch</h2>
              
              <div className="space-y-8">
                <div
                  className="p-6 hover-lift"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="p-3 flex-shrink-0"
                      style={{ border: '1px solid var(--border-medium)' }}
                    >
                      <Phone size={24} style={{ color: 'var(--text-primary)' }} />
                    </div>
                    <div>
                      <h3 className="heading-3 mb-2">Phone</h3>
                      <a
                        href={`tel:${hotelInfo.contact.phone}`}
                        className="body-regular block"
                        style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                      >
                        {hotelInfo.contact.phone}
                      </a>
                      <a
                        href={`tel:${hotelInfo.contact.phone2}`}
                        className="body-regular block"
                        style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                      >
                        {hotelInfo.contact.phone2}
                      </a>
                    </div>
                  </div>
                </div>

                <div
                  className="p-6 hover-lift"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="p-3 flex-shrink-0"
                      style={{ border: '1px solid var(--border-medium)' }}
                    >
                      <Mail size={24} style={{ color: 'var(--text-primary)' }} />
                    </div>
                    <div>
                      <h3 className="heading-3 mb-2">Email</h3>
                      <a
                        href={`mailto:${hotelInfo.contact.email}`}
                        className="body-regular block"
                        style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                      >
                        {hotelInfo.contact.email}
                      </a>
                      <a
                        href={`mailto:${hotelInfo.contact.email2}`}
                        className="body-regular block"
                        style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                      >
                        {hotelInfo.contact.email2}
                      </a>
                    </div>
                  </div>
                </div>

                <div
                  className="p-6 hover-lift"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-light)'
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="p-3 flex-shrink-0"
                      style={{ border: '1px solid var(--border-medium)' }}
                    >
                      <MapPin size={24} style={{ color: 'var(--text-primary)' }} />
                    </div>
                    <div>
                      <h3 className="heading-3 mb-2">Location</h3>
                      <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                        {hotelInfo.location.address}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div
              className="p-8"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-light)'
              }}
            >
              <h2 className="heading-2 mb-6">Send us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      required
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      required
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    style={{ borderRadius: 0 }}
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    style={{ borderRadius: 0 }}
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message</Label>
                  <textarea
                    id="message"
                    required
                    rows={5}
                    className="w-full p-3 border"
                    style={{
                      borderColor: 'var(--border-medium)',
                      borderRadius: 0,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '14px'
                    }}
                  />
                </div>

                <Button type="submit" className="btn-primary w-full" disabled={sending}>
                  {sending ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
